import { useState } from 'react';
import { identify } from '../lib/track';
import { AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { useSeo } from '../lib/useSeo';

// Login is optional everywhere except /admin (see AdminLayout) — this page
// mainly exists so staff can reach the admin area and so shoppers who want
// order history have somewhere to sign up. Guest checkout never routes here.
// Modes: 'signin' | 'signup' | 'reset' (forgot password → emails a link that
// lands on /reset-password).
export default function LoginPage() {
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/';

  useSeo({
    title: 'Sign In · Shiny Vault LGS',
    description: 'Sign in or create a Shiny Vault LGS account to track orders and manage your wishlist.',
    url: 'https://shinyvaultlgs.com/login',
  });

  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const clearMsgs = () => { setError(''); setNotice(''); };

  // Auth failures used to surface as a bare one-line message, and anything that
  // *threw* (network drop, CORS, a 500 out of GoTrue) showed nothing at all
  // because there was no catch. Both made real problems undiagnosable — a
  // malformed auth.users row once returned "Database error querying schema"
  // with no hint that it was server-side. Keep the status/code visible.
  const describe = (err) => {
    if (!err) return 'Something went wrong. Please try again.';
    const base = err.message || String(err);
    const bits = [];
    if (err.status) bits.push(`HTTP ${err.status}`);
    if (err.code && err.code !== err.status) bits.push(err.code);
    const detail = bits.length ? ` (${bits.join(' · ')})` : '';
    if (err.status >= 500) {
      return `${base}${detail} — this is a server-side error, not a wrong password. Send this to Chase.`;
    }
    return `${base}${detail}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    clearMsgs();
    setBusy(true);
    try {
      if (mode === 'reset') {
        const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (err) { setError(describe(err)); return; }
        setNotice('If an account exists for that email, a password reset link is on its way. Check your inbox (and spam).');
        return;
      }

      if (mode === 'signin') {
        const { data, error: err } = await signIn(email.trim(), password);
        if (err) { setError(describe(err)); return; }
        // Ties the anonymous half of this visit to a real person.
        identify(email.trim(), data?.user?.id ?? null);
        // If we were sent here to reach a specific page, honor that. Otherwise
        // route by role: staff land in the back area, customers in their orders.
        if (next && next !== '/') {
          navigate(next, { replace: true });
        } else {
          const u = data?.user;
          let isStaff = false;
          if (u) {
            // Shiny Vault's own admin flag — a Trainer Center admin is a
            // customer here, and lands in /account like anyone else.
            const { data: prof } = await supabase
              .from('profiles').select('is_shinyvault_admin').eq('id', u.id).maybeSingle();
            isStaff = !!prof?.is_shinyvault_admin;
          }
          navigate(isStaff ? '/admin/orders' : '/account/orders', { replace: true });
        }
      } else {
        const { error: err } = await signUp(email.trim(), password);
        if (err) { setError(describe(err)); return; }
        setNotice('Account created. Check your email to confirm, then sign in.');
        setMode('signin');
      }
    } catch (err) {
      console.error('[login]', err);
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  };

  // Set by AuthContext when it clears a session the server rejected. Read it
  // once so a refresh does not keep re-announcing it.
  const [expired] = useState(() => {
    try {
      const hit = sessionStorage.getItem('sv_session_expired') === '1';
      if (hit) sessionStorage.removeItem('sv_session_expired');
      return hit;
    } catch (_e) { return false; }
  });

  const heading = mode === 'signin' ? 'Log in' : mode === 'signup' ? 'Create an account' : 'Reset your password';
  const submitLabel = busy ? 'Please wait…' : mode === 'signin' ? 'Log in' : mode === 'signup' ? 'Sign up' : 'Send reset link';

  return (
    <div className="sv-container sv-page-top sv-page-bottom" style={{ maxWidth: 400 }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 900, marginBottom: 6 }}>{heading}</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginBottom: 24 }}>
        {mode === 'reset'
          ? 'Enter your email and we’ll send a link to set a new password.'
          : <>Not required to shop — <Link to="/shop" style={{ textDecoration: 'underline' }}>continue as a guest</Link> instead.</>}
      </p>

      {expired && (
        <div
          role="status"
          style={{
            display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 18,
            fontSize: '0.85rem', lineHeight: 1.45, padding: '10px 12px', borderRadius: 8,
            border: '1px solid var(--warning)', color: 'var(--warning)',
            background: 'color-mix(in srgb, var(--warning) 10%, transparent)',
          }}
        >
          <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>
            <strong>Your session expired.</strong> Log in again and you&rsquo;ll go straight
            back to what you were doing.
          </span>
        </div>
      )}

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <input
          className="sv-input"
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />

        {mode !== 'reset' && (
          <>
            <div style={{ position: 'relative' }}>
              <input
                className="sv-input"
                type={showPw ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                minLength={6}
                required
                style={{ width: '100%', paddingRight: 46 }}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? 'Hide password' : 'Show password'}
                title={showPw ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
                  display: 'flex', alignItems: 'center', padding: 6,
                }}
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {mode === 'signin' && (
              <button
                type="button"
                onClick={() => { setMode('reset'); clearMsgs(); }}
                style={{
                  alignSelf: 'flex-end', background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', fontSize: '0.8rem', textDecoration: 'underline', padding: 0,
                }}
              >
                Forgot password?
              </button>
            )}
          </>
        )}

        {error && (
          <div
            role="alert"
            style={{
              display: 'flex', gap: 8, alignItems: 'flex-start', color: 'var(--danger)',
              fontSize: '0.85rem', lineHeight: 1.45, padding: '10px 12px', borderRadius: 8,
              border: '1px solid var(--danger)', background: 'color-mix(in srgb, var(--danger) 8%, transparent)',
            }}
          >
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{error}</span>
          </div>
        )}
        {notice && (
          <div
            style={{
              display: 'flex', gap: 8, alignItems: 'flex-start', color: 'var(--success)',
              fontSize: '0.85rem', lineHeight: 1.45, padding: '10px 12px', borderRadius: 8,
              border: '1px solid var(--success)', background: 'color-mix(in srgb, var(--success) 8%, transparent)',
            }}
          >
            <CheckCircle2 size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{notice}</span>
          </div>
        )}

        <button type="submit" className="sv-btn" disabled={busy} style={{ marginTop: 4 }}>
          {submitLabel}
        </button>
      </form>

      {mode === 'reset' ? (
        <button
          onClick={() => { setMode('signin'); clearMsgs(); }}
          className="sv-btn-outline"
          style={{ width: '100%', marginTop: 10, padding: 10, fontSize: '0.85rem' }}
        >
          Back to log in
        </button>
      ) : (
        <button
          onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); clearMsgs(); }}
          className="sv-btn-outline"
          style={{ width: '100%', marginTop: 10, padding: 10, fontSize: '0.85rem' }}
        >
          {mode === 'signin' ? "Don't have an account? Sign up" : 'Already have an account? Log in'}
        </button>
      )}
    </div>
  );
}
